import { useEffect, useRef } from "react";
import { usePrivy, type User } from "@privy-io/react-auth";
import { useExportWallet, useSignTransaction, useWallets } from "@privy-io/react-auth/solana";
import { env, solanaChain } from "../env";

export interface PrivyApi {
  ready: boolean;
  authenticated: boolean;
  /** The embedded Solana wallet address, once created. */
  embeddedAddress: string | null;
  login(): void;
  logout(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  signTransaction(bytes: Uint8Array): Promise<Uint8Array>;
  exportKey(): Promise<void>;
}

function embeddedSolanaAddress(user: User | null): string | null {
  for (const a of user?.linkedAccounts ?? []) {
    if (a.type === "wallet" && "chainType" in a && a.chainType === "solana" && "walletClientType" in a && a.walletClientType === "privy") {
      return a.address;
    }
  }
  return null;
}

/** Privy hooks live here so the rest of the app works without a Privy app id. */
export function PrivyBridge({ onChange }: { onChange: (api: PrivyApi) => void }) {
  const privy = usePrivy();
  const { wallets } = useWallets();
  const { signTransaction } = useSignTransaction();
  const { exportWallet } = useExportWallet();

  // Hook functions aren't guaranteed stable; read the latest through a ref so the
  // published API only changes when ready/authenticated/address change.
  const latest = useRef({ privy, wallets, signTransaction, exportWallet });
  latest.current = { privy, wallets, signTransaction, exportWallet };

  const embeddedAddress = embeddedSolanaAddress(privy.user);
  const { ready, authenticated } = privy;

  useEffect(() => {
    onChange({
      ready,
      authenticated,
      embeddedAddress,
      login: () => latest.current.privy.login(),
      logout: () => latest.current.privy.logout(),
      getAccessToken: () => latest.current.privy.getAccessToken(),
      async signTransaction(bytes) {
        const wallet = latest.current.wallets.find((w) => w.address === embeddedAddress);
        if (!wallet) throw new Error("Your game wallet isn't ready yet");
        const chain = env.cluster === "localnet" ? undefined : (solanaChain as "solana:mainnet" | "solana:devnet");
        const { signedTransaction } = await latest.current.signTransaction({ transaction: bytes, wallet, ...(chain ? { chain } : {}) });
        return new Uint8Array(signedTransaction);
      },
      async exportKey() {
        if (!embeddedAddress) throw new Error("No game wallet to export");
        await latest.current.exportWallet({ address: embeddedAddress });
      },
    });
  }, [ready, authenticated, embeddedAddress, onChange]);

  return null;
}
